/// <reference lib="dom" />
/**
 * userSettings.ts
 * User preference storage — UI toggles that persist across sessions but are
 * separate from game progress (which lives in saveManager).
 *
 * This is a small in-memory cache backed by `localStorage`. Read with
 * `getUserSetting()` (synchronous; reads from cache populated at app start
 * via `loadUserSettings()`), write with `setUserSetting()` (writes through
 * to `localStorage` synchronously inside an async wrapper).
 *
 * DECISION: Synchronous reads via cache because settings are queried at
 * render time (e.g. "should the keybinding overlay be visible?"). An async
 * read would force every consumer through a Promise + loading state.
 *
 * @module userSettings
 */

import Logger from '../utils/logger';
import { getStorage } from './storageBackend';

/** `localStorage` key for the user settings blob. */
const STORAGE_KEY = '@starfall_courier:settings';

/** All persisted user settings. Add new fields here with safe defaults. */
export interface UserSettings {
  /**
   * Whether to show the on-screen D-pad and action buttons during gameplay.
   * Default is touch-aware: on for touch-capable devices, off for keyboard-only.
   * Resolved by getDefaultUserSettings() at first run.
   */
  showTouchControls: boolean;
  /**
   * Whether to force greybox rendering instead of sprite-based visuals.
   * Off by default. Toggle from Settings → Display for a retro/debug view.
   */
  useGreyboxMode: boolean;
  /**
   * Dev-only: when true, World 2 (Starfield Drift) is selectable from the
   * world map even though the World 1 finale + cross-world unlock gate
   * haven't shipped yet. Off by default. Toggle from Settings → Developer.
   */
  devUnlockWorld2: boolean;
  /**
   * Dev-only: when true, two W3-onboarding gates are bypassed at once:
   * 1. World 3 (Brasscloud Foundry) becomes selectable from the world map.
   * 2. Nova starts every level with `canDoubleJump` enabled, mirroring
   *    the post-W2-boss ability unlock.
   * Off by default. Toggle from Settings → Developer. The save-data path
   * (write on 2-B clear, read in PlayScene init) still applies in
   * parallel — this just bypasses both gates so the W3 greybox is
   * exercisable before the boss ships.
   */
  devUnlockWorld3: boolean;
  /**
   * Dev-only: when true, Nova starts every level with `canWallSlide`
   * enabled, mirroring the World 4 unlock without requiring the player to
   * reach World 4 (per AGENTS.md: wall slide unlocks at the start of World
   * 4). Off by default. Toggle from Settings → Developer. The save-data
   * path (write on World 4 entry, read in PlayScene init) still applies in
   * parallel — this just bypasses the gate.
   */
  devUnlockWorld4: boolean;
  /**
   * Dev-only: when true, Nova fires Gem Shard projectiles instead of Star
   * Shards. Gem Shards travel 1.5x faster (greybox preview of the W3+
   * weapon-pickup variant). Off by default. The pickup-collection path
   * lands with the W3 levels; this toggle lets the variant be exercised
   * in W1 until then. Toggle from Settings → Developer.
   */
  devUseGemShard: boolean;
}

/**
 * Detect whether the current browser session has touch capability.
 * Returns `true` for any touch-capable browser — phones, tablets, and
 * touch-enabled laptops/desktops (e.g. Windows 2-in-1 devices). This is
 * intentional: if the device supports touch, we show the D-pad by default
 * because the user can interact that way. Users on touch desktops who prefer
 * keyboard can toggle touch controls off in Settings.
 *
 * Returns `false` in SSR/test environments where `navigator` may not exist.
 */
// DECISION: Combine (pointer: coarse) media query with maxTouchPoints so we
// catch *both* primary-touch devices (phones/tablets) and hybrid laptops with
// touch screens but a mouse as primary pointer (Surface, 2-in-1s). Pointer
// (coarse) alone misses hybrids; maxTouchPoints alone false-positives on
// Windows desktops that report >0 for pen/stylus without a touch screen.
// OR-ing both signals is more inclusive than either alone and still excludes
// pure desktops. Falls back to maxTouchPoints when matchMedia is unavailable.
export function isTouchCapableWeb(): boolean {
  const win = globalThis as { matchMedia?: (q: string) => { matches: boolean } };
  const nav = globalThis.navigator as { maxTouchPoints?: number } | undefined;
  const hasTouchPoints = nav != null && (nav.maxTouchPoints ?? 0) > 0;
  if (typeof win.matchMedia === 'function') {
    return win.matchMedia('(pointer: coarse)').matches || hasTouchPoints;
  }
  // Fallback for environments without matchMedia.
  return hasTouchPoints;
}

/**
 * Default settings, computed once. Touch-aware so we don't ship the D-pad
 * on top of the canvas on a desktop browser by default.
 *
 * DECISION: factor function so loadUserSettings() and the test reset hook
 * read the same source of truth, avoiding a hardcoded literal.
 */
function getDefaultUserSettings(): UserSettings {
  return {
    // On only if the browser reports touch support.
    showTouchControls: isTouchCapableWeb(),
    useGreyboxMode: false,
    devUnlockWorld2: false,
    devUnlockWorld3: false,
    devUnlockWorld4: false,
    devUseGemShard: false,
  };
}

const DEFAULT_SETTINGS: UserSettings = getDefaultUserSettings();

/** Module-level cache. Populated by loadUserSettings(); read by getUserSetting(). */
let _cache: UserSettings = { ...DEFAULT_SETTINGS };

/** Subscribers notified after every setUserSetting() call. */
const _listeners = new Set<() => void>();

/**
 * Load settings from `localStorage` into the cache. Call once at app start
 * so synchronous reads work everywhere.
 *
 * Failures fall back to defaults silently — corrupt settings shouldn't crash
 * the app.
 */
export async function loadUserSettings(): Promise<void> {
  const storage = getStorage();
  try {
    const raw = storage?.getItem(STORAGE_KEY) ?? null;
    if (raw) {
      const parsed: Record<string, unknown> = JSON.parse(raw) as Record<string, unknown>;
      // DECISION: Validate each field type before merging so corrupt values
      // (e.g. "false" string instead of boolean) don't leak into the cache.
      const validated: Partial<UserSettings> = {};
      for (const key of Object.keys(DEFAULT_SETTINGS) as (keyof UserSettings)[]) {
        if (key in parsed && typeof parsed[key] === typeof DEFAULT_SETTINGS[key]) {
          (validated as Record<string, unknown>)[key] = parsed[key];
        }
      }
      _cache = { ...DEFAULT_SETTINGS, ...validated };
    } else {
      // No stored value — reset cache to defaults to clear any stale in-memory state.
      _cache = { ...DEFAULT_SETTINGS };
    }
  } catch (err) {
    Logger.warn('userSettings', 'Failed to load settings, using defaults', err);
    _cache = { ...DEFAULT_SETTINGS };
  }
  // Notify subscribers so components mounted before load see persisted values.
  for (const fn of _listeners) fn();
}

/** Read one setting from the in-memory cache. */
export function getUserSetting<K extends keyof UserSettings>(key: K): UserSettings[K] {
  return _cache[key];
}

/**
 * Write one setting through to `localStorage`. The cache updates immediately
 * so the next render sees the new value; the storage write is wrapped in
 * try/catch so quota errors don't crash the caller.
 */
export function setUserSetting<K extends keyof UserSettings>(
  key: K,
  value: UserSettings[K],
): void {
  _cache = { ..._cache, [key]: value };
  for (const fn of _listeners) fn();
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(_cache));
  } catch (err) {
    Logger.warn('userSettings', 'Failed to persist setting', err);
  }
}

/**
 * Subscribe to setting changes. Returns an unsubscribe function.
 * Used by React components that need to re-render when a setting flips.
 */
export function subscribeUserSettings(fn: () => void): () => void {
  _listeners.add(fn);
  return (): void => { _listeners.delete(fn); };
}

/** Test-only: reset the in-memory cache to fresh defaults (re-evaluates platform). */
export function _resetUserSettingsForTesting(): void {
  _cache = getDefaultUserSettings();
  _listeners.clear();
}
